import { Link } from '@remix-run/react';
import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { CalendarEarning } from '~/types/earningsCalendar.types';

import { tickerLogoUrl } from '../utils/tickerLogoUrl';

const OVERFLOW_ROW_CLASS =
  'flex w-full min-w-0 items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-emerald-50/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-emerald-600';

function OverflowLogo({ item }: { item: CalendarEarning }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const initials = item.symbol.slice(0, 2).toUpperCase();
  const logoSrc = tickerLogoUrl(item);

  useEffect(() => {
    setLogoFailed(false);
  }, [item.symbol, logoSrc]);

  return (
    <span className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-700">
      {!logoFailed ? (
        <img
          key={`${item.symbol}-${logoSrc}`}
          src={logoSrc}
          alt=""
          className="size-4 object-contain"
          loading="lazy"
          onError={() => setLogoFailed(true)}
        />
      ) : (
        <span className="flex size-full items-center justify-center text-[9px] font-bold text-slate-300">
          {initials}
        </span>
      )}
    </span>
  );
}

function OverflowRow({ item }: { item: CalendarEarning }) {
  const content = (
    <>
      <OverflowLogo item={item} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12px] font-semibold leading-snug text-[#1c1c1c]">
          {item.name}
        </span>
        <span className="mt-0.5 block text-[11px] font-medium tabular-nums tracking-tight text-slate-500">
          {item.symbol}
          {item.formattedMarketCap ? (
            <span className="text-slate-400"> · {item.formattedMarketCap}</span>
          ) : null}
        </span>
      </span>
    </>
  );

  if (!item.tickerHref) {
    return (
      <span className={OVERFLOW_ROW_CLASS} title={item.name}>
        {content}
      </span>
    );
  }

  return (
    <Link to={item.tickerHref} className={OVERFLOW_ROW_CLASS} title={item.name}>
      {content}
    </Link>
  );
}

export default function MoreCompaniesMenu({ items }: { items: CalendarEarning[] }) {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open || !buttonRef.current) {
      setMenuPosition(null);
      return;
    }

    const updatePosition = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 280;
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - menuWidth - 8));
      setMenuPosition({ top: rect.bottom + 6, left });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target) && !buttonRef.current?.contains(target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div className="relative flex h-full min-w-0 items-center justify-center">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={open ? 'Hide more companies' : `See ${items.length} more companies`}
        onClick={() => setOpen((prev) => !prev)}
        className={`inline-flex items-center justify-center gap-1 rounded-full border px-2.5 py-2.5 shadow-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-emerald-600 md:px-3 md:py-3 ${
          open
            ? 'border-emerald-800 bg-emerald-800 text-white'
            : 'border-emerald-700 bg-emerald-700 text-white hover:border-emerald-800 hover:bg-emerald-800'
        }`}
      >
        <span className="text-[11px] font-bold leading-none tabular-nums tracking-tight md:text-[12px]">
          +{items.length}
        </span>
        <span className="text-[9px] font-semibold uppercase leading-none tracking-[0.06em] text-white/90 md:text-[10px]">
          {open ? 'Close' : 'More'}
        </span>
        <ChevronDown
          className={`size-3 shrink-0 text-white/90 transition-transform duration-200 md:size-3.5 ${
            open ? 'rotate-180' : ''
          }`}
          strokeWidth={2.5}
          aria-hidden
        />
      </button>

      {open && menuPosition ? (
        <div
          ref={menuRef}
          role="listbox"
          aria-label="More companies reporting"
          className="fixed z-50 w-[min(280px,calc(100vw-16px))] rounded-xl border border-slate-900/[0.08] bg-white p-1.5 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.25)]"
          style={{ top: menuPosition.top, left: menuPosition.left }}
        >
          <div className="flex max-h-64 flex-col gap-0.5 overflow-y-auto">
            {items.map((item, i) => (
              <OverflowRow key={`${item.symbol}-${item.name}-overflow-${i}`} item={item} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
